// ============================================================================
// USE TYPE FILTER CONFIG HOOK
// Fetches work_item_number_sequences to get which item types have
// show_in_type_filter enabled for dynamic navigation filter tabs
// Date: January 20, 2026
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { authService } from '@/lib/authService';
import { resolveSequenceConfig } from '@/lib/work-items/sequenceScope';
import * as LucideIcons from 'lucide-react';
import {
  Briefcase,
  FolderKanban,
  Calendar,
  Ticket,
  Package,
  Truck,
  ClipboardCheck,
  AlertTriangle,
  Wrench,
  Route,
  FileText,
  Bug,
  Zap,
  Target,
  Flag,
  Repeat,
  type LucideIcon
} from 'lucide-react';

const LUCIDE_ICON_MAP = LucideIcons as unknown as Record<string, LucideIcon>;

// Icon mapping for item types
const ITEM_TYPE_ICONS: Record<string, any> = {
  job: Briefcase,
  project: FolderKanban,
  appointment: Calendar,
  ticket: Ticket,
  asset: Package,
  delivery: Truck,
  task: ClipboardCheck,
  inspection: ClipboardCheck,
  incident: AlertTriangle,
  maintenance: Wrench,
  route: Route,
  manifest: FileText,
  bug: Bug,
  story: Zap,
  feature: Target,
  milestone: Flag,
  sprint: Target,
  epic: FolderKanban,
  recurring_job: Repeat,
  request: FileText,
  work_order: Wrench,
  billing_submission: ClipboardCheck,
  follow_up: Flag,
};

// Color mapping for item types
const ITEM_TYPE_COLORS: Record<string, { color: string; hoverColor: string }> = {
  job: { color: 'bg-blue-500', hoverColor: 'hover:bg-blue-600' },
  project: { color: 'bg-emerald-500', hoverColor: 'hover:bg-emerald-600' },
  appointment: { color: 'bg-purple-500', hoverColor: 'hover:bg-purple-600' },
  ticket: { color: 'bg-orange-500', hoverColor: 'hover:bg-orange-600' },
  asset: { color: 'bg-cyan-500', hoverColor: 'hover:bg-cyan-600' },
  delivery: { color: 'bg-amber-500', hoverColor: 'hover:bg-amber-600' },
  task: { color: 'bg-indigo-500', hoverColor: 'hover:bg-indigo-600' },
  inspection: { color: 'bg-teal-500', hoverColor: 'hover:bg-teal-600' },
  incident: { color: 'bg-red-500', hoverColor: 'hover:bg-red-600' },
  maintenance: { color: 'bg-slate-500', hoverColor: 'hover:bg-slate-600' },
  route: { color: 'bg-lime-500', hoverColor: 'hover:bg-lime-600' },
  manifest: { color: 'bg-fuchsia-500', hoverColor: 'hover:bg-fuchsia-600' },
  bug: { color: 'bg-red-500', hoverColor: 'hover:bg-red-600' },
  story: { color: 'bg-sky-500', hoverColor: 'hover:bg-sky-600' },
  feature: { color: 'bg-violet-500', hoverColor: 'hover:bg-violet-600' },
  milestone: { color: 'bg-rose-500', hoverColor: 'hover:bg-rose-600' },
  sprint: { color: 'bg-green-500', hoverColor: 'hover:bg-green-600' },
  epic: { color: 'bg-pink-500', hoverColor: 'hover:bg-pink-600' },
  recurring_job: { color: 'bg-purple-500', hoverColor: 'hover:bg-purple-600' },
  request: { color: 'bg-orange-600', hoverColor: 'hover:bg-orange-700' },
  work_order: { color: 'bg-slate-500', hoverColor: 'hover:bg-slate-600' },
  billing_submission: { color: 'bg-teal-600', hoverColor: 'hover:bg-teal-700' },
  follow_up: { color: 'bg-rose-500', hoverColor: 'hover:bg-rose-600' },
};

// Labels for item types
const ITEM_TYPE_LABELS: Record<string, string> = {
  job: 'Jobs',
  project: 'Projects',
  appointment: 'Appointments',
  ticket: 'Tickets',
  asset: 'Assets',
  delivery: 'Deliveries',
  task: 'Tasks',
  inspection: 'Inspections',
  incident: 'Incidents',
  maintenance: 'Maintenance',
  route: 'Routes',
  manifest: 'Manifests',
  bug: 'Bugs',
  story: 'Stories',
  feature: 'Features',
  milestone: 'Milestones',
  sprint: 'Sprints',
  epic: 'Epics',
  recurring_job: 'Recurring Jobs',
  request: 'Job Requests',
  work_order: 'Work Orders',
  billing_submission: 'Billing Submissions',
  follow_up: 'Follow-Ups',
};

// Descriptions for item types
const ITEM_TYPE_DESCRIPTIONS: Record<string, string> = {
  job: 'Service jobs and work orders',
  project: 'Projects with budgets',
  appointment: 'Scheduled appointments',
  ticket: 'Support tickets',
  asset: 'Equipment and vehicles',
  delivery: 'Delivery notes',
  task: 'Project tasks',
  inspection: 'Site inspections',
  incident: 'Safety incidents',
  maintenance: 'Maintenance records',
  route: 'Delivery routes',
  manifest: 'Cargo manifests',
  bug: 'Bug reports',
  story: 'User stories',
  feature: 'Feature requests',
  milestone: 'Project milestones',
  sprint: 'Development sprints',
  epic: 'Large feature groups',
  recurring_job: 'Recurring job schedules',
  request: 'Job requests created from approved proposals',
  work_order: 'Operational work orders for maintenance and field service execution',
  billing_submission: 'Operational billing packs submitted to finance for review and conversion',
  follow_up: 'Follow-up visits and queries raised against work items',
};

// Default item types if API fails (backwards compatibility)
const DEFAULT_TYPE_FILTER_ENABLED: string[] = ['job', 'project'];

const MODULE_DEFAULT_TYPE_FILTERS: Record<string, string[]> = {
  'software-development': ['bug', 'feature', 'story', 'epic', 'sprint', 'request'],

  'standups-meetings': ['task', 'bug', 'feature', 'story', 'sprint', 'request'],
};

const MODULE_HIDDEN_TYPE_FILTERS: Record<string, string[]> = {
  'software-development': ['task', 'recurring_job'],
};

export interface TypeFilterOption {
  id: string;
  label: string;
  singularLabel?: string;
  icon: any;
  color: string;
  hoverColor: string;
  description: string;
}

interface SequenceConfig {
  item_type: string;
  /** 'default' (or missing pre-migration) = client-wide row; other values are
   * workflow keys carrying scoped config overrides. */
  workflow_context?: string;
  config?: {
    visible_tabs?: string[];
    show_in_type_filter?: boolean;
    type_filter_order?: number;
  };
}

interface UseTypeFilterConfigResult {
  typeFilterOptions: TypeFilterOption[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface WorkItemTypeDefinition {
  type_key: string;
  type_label?: string;
  type_label_plural?: string;
  description?: string;
  icon?: string | null;
}

const humanizeItemType = (itemType: string): string => {
  return itemType
    .split(/[_-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const resolveLucideIcon = (iconName?: string | null): LucideIcon | null => {
  if (!iconName) return null;

  const raw = iconName.trim();
  if (!raw) return null;

  const pascalCase = raw
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

  const candidates = [
    raw,
    raw.charAt(0).toUpperCase() + raw.slice(1),
    pascalCase,
  ];

  for (const candidate of candidates) {
    const icon = LUCIDE_ICON_MAP[candidate];
    if (icon) {
      return icon;
    }
  }

  return null;
};

const buildTypeFilterOption = (
  id: string,
  typeDefinitions: Map<string, WorkItemTypeDefinition>
): TypeFilterOption => {
  const definition = typeDefinitions.get(id);
  const resolvedIcon = resolveLucideIcon(definition?.icon);

  return {
    id,
    label:
      definition?.type_label_plural ||
      definition?.type_label ||
      ITEM_TYPE_LABELS[id] ||
      humanizeItemType(id),
    singularLabel:
      definition?.type_label ||
      humanizeItemType(id),
    icon: resolvedIcon || ITEM_TYPE_ICONS[id] || Briefcase,
    color: ITEM_TYPE_COLORS[id]?.color || 'bg-gray-500',
    hoverColor: ITEM_TYPE_COLORS[id]?.hoverColor || 'hover:bg-gray-600',
    description: definition?.description || ITEM_TYPE_DESCRIPTIONS[id] || '',
  };
};

const buildDefaultTypeFilterOptions = (
  typeDefinitions: Map<string, WorkItemTypeDefinition> = new Map(),
  defaultTypeIds: string[] = DEFAULT_TYPE_FILTER_ENABLED,
): TypeFilterOption[] => {
  return defaultTypeIds.map(id => buildTypeFilterOption(id, typeDefinitions));
};

export function useTypeFilterConfig(moduleContext?: string): UseTypeFilterConfigResult {
  const [typeFilterOptions, setTypeFilterOptions] = useState<TypeFilterOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const normalizedModuleContext = (moduleContext || '').trim().toLowerCase();
      const contextualDefaults = MODULE_DEFAULT_TYPE_FILTERS[normalizedModuleContext] || DEFAULT_TYPE_FILTER_ENABLED;
      const hiddenTypeIds = new Set(MODULE_HIDDEN_TYPE_FILTERS[normalizedModuleContext] || []);
      const visibleContextualDefaults = contextualDefaults.filter((type) => !hiddenTypeIds.has(type));

      const token = authService.getTokens()?.access_token;
      if (!token) {
        // Fallback to defaults if not authenticated
        setTypeFilterOptions(buildDefaultTypeFilterOptions(new Map(), visibleContextualDefaults));
        return;
      }

      const [response, typeDefinitionsResponse] = await Promise.all([
        fetch('/api/v1/admin/work-item-sequences', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch('/api/v1/work-item-types', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (!response.ok) {
        throw new Error('Failed to fetch type filter configuration');
      }

      const data = await response.json();
      const sequences: SequenceConfig[] = data.data || [];
      let typeDefinitions = new Map<string, WorkItemTypeDefinition>();

      if (typeDefinitionsResponse.ok) {
        const typeDefinitionsData = await typeDefinitionsResponse.json();
        const types: WorkItemTypeDefinition[] = Array.isArray(typeDefinitionsData.types)
          ? typeDefinitionsData.types
          : [];

        typeDefinitions = new Map(types.map(type => [type.type_key, type]));
      }

      // Get item types with show_in_type_filter enabled, along with their
      // order. Rows are workflow-scoped: a row matching the current
      // moduleContext wins over the 'default' row for the same item_type.
      const enabledTypes: { id: string; order: number }[] = [];
      const uniqueItemTypes = [...new Set(sequences.map((seq) => seq.item_type))];

      uniqueItemTypes.forEach((itemType) => {
        const effectiveConfig = resolveSequenceConfig(sequences, itemType, normalizedModuleContext);
        if (effectiveConfig?.show_in_type_filter === true) {
          enabledTypes.push({
            id: itemType,
            order: typeof effectiveConfig.type_filter_order === 'number' ? effectiveConfig.type_filter_order : 999,
          });
        }
      });

      // If no types have explicit config, check for defaults
      // (for backwards compatibility with existing data that doesn't have config yet)
      if (enabledTypes.length === 0) {
        // Check if any sequences exist at all
        const hasAnySequences = sequences.length > 0;

        if (hasAnySequences) {
          // Sequences exist but none have show_in_type_filter, use defaults
          visibleContextualDefaults.forEach((type, idx) => {
            const effectiveConfig = resolveSequenceConfig(sequences, type, normalizedModuleContext);
            // Only add if no explicit config set to false
            if (effectiveConfig?.show_in_type_filter !== false) {
              enabledTypes.push({ id: type, order: idx });
            }
          });
        } else {
          // No sequences at all, use defaults
          visibleContextualDefaults.forEach((type, idx) =>
            enabledTypes.push({ id: type, order: idx })
          );
        }
      } else if (visibleContextualDefaults.length > 0) {
        // For module-specific contexts (e.g. software-development), augment sparse
        // sequence configs with expected defaults unless explicitly hidden.
        const enabledTypeIds = new Set(enabledTypes.map((entry) => entry.id));

        visibleContextualDefaults.forEach((type, idx) => {
          if (enabledTypeIds.has(type)) {
            return;
          }

          const effectiveConfig = resolveSequenceConfig(sequences, type, normalizedModuleContext);
          if (effectiveConfig?.show_in_type_filter === false) {
            return;
          }

          enabledTypes.push({
            id: type,
            order: typeof effectiveConfig?.type_filter_order === 'number' ? effectiveConfig.type_filter_order : (100 + idx),
          });
        });
      }

      const visibleEnabledTypes = enabledTypes.filter((entry) => !hiddenTypeIds.has(entry.id));

      // Sort by configured order
      visibleEnabledTypes.sort((a, b) => a.order - b.order);

      // Convert to TypeFilterOption format
      const options: TypeFilterOption[] = visibleEnabledTypes.map(({ id }) =>
        buildTypeFilterOption(id, typeDefinitions)
      );

      setTypeFilterOptions(options);
    } catch (err: any) {
      console.error('Error fetching type filter config:', err);
      setError(err.message);

      // Fallback to defaults on error
      const normalizedModuleContext = (moduleContext || '').trim().toLowerCase();
      const contextualDefaults = MODULE_DEFAULT_TYPE_FILTERS[normalizedModuleContext] || DEFAULT_TYPE_FILTER_ENABLED;
      const hiddenTypeIds = new Set(MODULE_HIDDEN_TYPE_FILTERS[normalizedModuleContext] || []);
      setTypeFilterOptions(buildDefaultTypeFilterOptions(new Map(), contextualDefaults.filter((type) => !hiddenTypeIds.has(type))));
    } finally {
      setLoading(false);
    }
  }, [moduleContext]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    typeFilterOptions,
    loading,
    error,
    refresh: fetchConfig
  };
}

export default useTypeFilterConfig;
